import { useEffect, useState } from "react";
import { Loader2, Play, Square, Save, Radio as RadioIcon, Image as ImageIcon, X } from "lucide-react";
import { toast } from "sonner";
import { adminApi, formatApiError } from "@/lib/api";
import { compressImage } from "@/lib/imageCompress";
import { ADMIN } from "@/constants/testIds";

const AdminRadio = () => {
  const [session, setSession] = useState(null);
  const [form, setForm] = useState({
    program_name: "",
    host: "",
    cover_url: "",
    stream_url: "",
  });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const { data } = await adminApi.get("/admin/radio/session");
    setSession(data);
    setForm({
      program_name: data.program_name || "",
      host: data.host || "",
      cover_url: data.cover_url || "",
      stream_url: data.stream_url || "",
    });
  };

  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    if (!form.program_name || !form.host || !form.stream_url) {
      toast.error("Preencha programa, apresentador e URL do stream.");
      return;
    }
    setBusy(true);
    try {
      await adminApi.post("/admin/radio/save", { ...form, is_live: !!session?.is_live });
      toast.success(session?.is_live ? "Alterações salvas. A transmissão continua no ar." : "Alterações salvas. Rádio permanece fora do ar.");
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const start = async () => {
    if (!form.program_name || !form.host || !form.stream_url) {
      toast.error("Preencha programa, apresentador e URL do stream.");
      return;
    }
    setBusy(true);
    try {
      await adminApi.post("/admin/radio/start", { ...form, is_live: true });
      toast.success("Transmissão iniciada.");
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const stop = async () => {
    if (!window.confirm("Encerrar a transmissão ao vivo?")) return;
    setBusy(true);
    try {
      await adminApi.post("/admin/radio/stop");
      toast.success("Transmissão encerrada.");
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <div className="text-xs uppercase tracking-[0.28em] text-[var(--fi-red)] font-bold mb-1">
        Transmissão
      </div>
      <h1 className="font-display text-4xl sm:text-5xl font-black uppercase text-[var(--fi-blue)] leading-tight">
        Rádio Ao Vivo
      </h1>
      <p className="text-gray-500 mt-1">
        Gerencie o card de "AO VIVO" que aparece no app público.
      </p>

      {session && (
        <div className={`mt-5 rounded-xl px-4 py-3 text-sm font-bold uppercase tracking-widest inline-flex items-center gap-2 ${
          session.is_live ? "bg-emerald-100 text-emerald-700" : "bg-gray-200 text-gray-600"
        }`}>
          <RadioIcon className="w-4 h-4" />
          Status: {session.is_live ? "no ar" : "fora do ar"}
        </div>
      )}

      <div className="mt-5 rounded-xl bg-white p-5 shadow-[0_8px_30px_rgba(13,27,75,0.06)] space-y-3">
        <Labeled label="Nome do programa *">
          <input data-testid={ADMIN.radio.programName} value={form.program_name} onChange={(e) => setForm({ ...form, program_name: e.target.value })} className="input" placeholder="Ex: Jornal da Manhã" />
        </Labeled>
        <Labeled label="Apresentador *">
          <input data-testid={ADMIN.radio.host} value={form.host} onChange={(e) => setForm({ ...form, host: e.target.value })} className="input" placeholder="Ex: Ana Beatriz" />
        </Labeled>
        <Labeled label="Capa do programa">
          {form.cover_url && (
            <div className="relative inline-block mb-2">
              <img src={form.cover_url} alt="Capa" className="h-28 w-28 rounded-lg object-cover border border-gray-200" />
              <button
                type="button"
                onClick={() => setForm({ ...form, cover_url: "" })}
                data-testid="admin-radio-cover-remove"
                className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-white shadow border border-gray-200 text-gray-500 hover:text-[var(--fi-red)] flex items-center justify-center"
                aria-label="Remover capa"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          )}
          <div className="flex flex-col sm:flex-row gap-2">
            <label
              data-testid="admin-radio-cover-file"
              className="flex-1 flex items-center gap-2 rounded-xl border border-dashed border-gray-300 hover:border-[var(--fi-blue)] px-3 py-2.5 cursor-pointer transition-colors"
            >
              <ImageIcon className="w-5 h-5 text-[var(--fi-blue)]" />
              <span className="text-sm text-gray-600">
                {form.cover_url && form.cover_url.startsWith("data:") ? "Trocar arquivo" : "Enviar arquivo JPG ou PNG"}
              </span>
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (!f) return;
                  if (!/^image\/(jpe?g|png|webp)$/i.test(f.type)) {
                    toast.error("Envie apenas JPG, PNG ou WEBP.");
                    return;
                  }
                  compressImage(f).catch(() => f).then((img) => {
                    if (img.size > 1.5 * 1024 * 1024) { toast.error("Imagem muito grande mesmo após compressão (máx 1,5 MB)."); return; }
                    const reader = new FileReader();
                    reader.onload = () => setForm((s) => ({ ...s, cover_url: String(reader.result || "") }));
                    reader.readAsDataURL(img);
                  });
                }}
                className="hidden"
              />
            </label>
            <input
              data-testid={ADMIN.radio.cover}
              value={form.cover_url && form.cover_url.startsWith("data:") ? "" : form.cover_url}
              onChange={(e) => setForm({ ...form, cover_url: e.target.value })}
              className="input flex-1"
              placeholder="…ou cole uma URL de imagem"
            />
          </div>
        </Labeled>
        <Labeled label="URL do stream de áudio *">
          <input data-testid={ADMIN.radio.streamUrl} value={form.stream_url} onChange={(e) => setForm({ ...form, stream_url: e.target.value })} className="input" placeholder="https://.../stream.mp3 ou .m3u8" />
          <div className="text-xs text-gray-500 mt-1">
            O player público usa esta URL automaticamente. Trocou de servidor? Só cole o novo link e clique em <b>Salvar alterações</b> — sem precisar tirar do ar.
          </div>
        </Labeled>

        <div className="flex flex-wrap gap-2 pt-2">
          <button data-testid="admin-radio-save-btn" disabled={busy} onClick={save} className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-[var(--fi-blue)] hover:bg-[var(--fi-blue-2)] text-white font-bold uppercase tracking-widest text-xs transition-colors disabled:opacity-60">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Salvar alterações
          </button>
          <button data-testid={ADMIN.radio.startBtn} disabled={busy} onClick={start} className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-[var(--fi-red)] hover:bg-[var(--fi-red-hover)] text-white font-bold uppercase tracking-widest text-xs transition-colors disabled:opacity-60">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
            Iniciar / Atualizar Live
          </button>
          <button data-testid={ADMIN.radio.stopBtn} disabled={busy || !session?.is_live} onClick={stop} className="inline-flex items-center gap-2 px-5 py-3 rounded-xl border border-[var(--fi-blue)] text-[var(--fi-blue)] font-bold uppercase tracking-widest text-xs hover:bg-[var(--fi-blue)] hover:text-white transition-colors disabled:opacity-40">
            <Square className="w-4 h-4" />
            Encerrar
          </button>
        </div>
        <style>{`.input{width:100%;padding:10px 12px;border:1px solid #e5e7eb;border-radius:10px;font-size:14px;outline:none;transition:border-color .15s}.input:focus{border-color:#0D1B4B}`}</style>
      </div>

      <div className="mt-4 rounded-xl bg-amber-50 border border-amber-200 p-4 text-sm text-amber-900">
        <b>Nota:</b> O navegador não transmite áudio diretamente. Este painel controla apenas o card "AO VIVO" do app —
        sua transmissão real deve estar rodando em um servidor Icecast/Ant Media/Zeno.fm cuja URL você cola no campo "stream".
      </div>
    </div>
  );
};

const Labeled = ({ label, children }) => (
  <div>
    <label className="block text-xs uppercase tracking-widest font-bold text-[var(--fi-blue)] mb-1.5">{label}</label>
    {children}
  </div>
);

export default AdminRadio;
